import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useGamingStore } from '../../store/gamingStore';
import { X, User, Loader2, ChevronDown, Search } from 'lucide-react';

interface Props {
  onClose: () => void;
}

const LOADERS = [
  { id: 'vanilla', label: 'Vanilla', color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10' },
  { id: 'forge', label: 'Forge', color: 'text-orange-400 border-orange-500/30 bg-orange-500/10' },
  { id: 'fabric', label: 'Fabric', color: 'text-blue-400 border-blue-500/30 bg-blue-500/10' },
  { id: 'quilt', label: 'Quilt', color: 'text-purple-400 border-purple-500/30 bg-purple-500/10' },
] as const;

type McVersionEntry = { id: string; type: string; releaseTime: string };

const CreateProfileModal: React.FC<Props> = ({ onClose }) => {
  const { createProfile, fetchProfiles, openProfile } = useGamingStore();

  const [profileName, setProfileName] = useState('');
  const [version, setVersion] = useState('1.20.4');
  const [loader, setLoader] = useState<'vanilla' | 'forge' | 'fabric' | 'quilt'>('vanilla');
  const [serverAddress, setServerAddress] = useState('');
  const [serverPort, setServerPort] = useState('25565');
  const [autoConnect, setAutoConnect] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  // Version dropdown state
  const [mcVersions, setMcVersions] = useState<McVersionEntry[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [versionFilter, setVersionFilter] = useState<'release' | 'snapshot' | 'all'>('release');
  const [versionSearch, setVersionSearch] = useState('');
  const [versionDropdownOpen, setVersionDropdownOpen] = useState(false);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  useEffect(() => {
    const api = (window as any).api;
    if (api?.getMinecraftVersions) {
      api.getMinecraftVersions().then((data: McVersionEntry[]) => {
        setMcVersions(Array.isArray(data) ? data : []);
      }).catch(() => {}).finally(() => setLoadingVersions(false));
    } else {
      setLoadingVersions(false);
    }
  }, []);

  useEffect(() => {
    if (versionDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, [versionDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setVersionDropdownOpen(false);
      }
    };
    if (versionDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [versionDropdownOpen]);

  const filteredVersions = mcVersions.filter((v) => {
    if (versionFilter !== 'all' && v.type !== versionFilter) return false;
    if (versionSearch && !v.id.toLowerCase().includes(versionSearch.toLowerCase())) return false;
    return true;
  });

  const handleCreate = async () => {
    if (!profileName.trim()) return;
    setIsCreating(true);

    try {
      const profile = await createProfile({
        name: profileName.trim(),
        source: 'custom',
        minecraft: {
          version,
          loader,
          loader_version: null,
        },
        server: {
          address: serverAddress.trim(),
          port: parseInt(serverPort) || 25565,
          auto_connect: autoConnect,
        },
      });

      if (profile) {
        await fetchProfiles();
        openProfile(profile.id);
        onClose();
      }
    } finally {
      setIsCreating(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-bg-secondary border border-border-color rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-color">
          <h2 className="text-base font-bold text-text-primary">Nouveau profil Minecraft</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5 max-h-[60vh] overflow-y-auto gaming-scrollbar">
          {/* Profile name */}
          <div>
            <label className="text-xs text-text-muted block mb-1.5">Nom du profil</label>
            <input
              type="text"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              placeholder="Mon serveur survival"
              autoFocus
              className="w-full px-3 py-2.5 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:border-gaming-accent outline-none transition-colors"
            />
          </div>

          <div className="rounded-lg border border-border-color bg-bg-primary p-3 flex items-start gap-2">
            <User className="w-4 h-4 mt-0.5 text-gaming-accent" />
            <div>
              <p className="text-xs font-medium text-text-primary">Profil personnalisé</p>
              <p className="text-[10px] text-text-muted">Les profils serveur sont créés depuis les paramètres du serveur Erozion.</p>
            </div>
          </div>

          {/* Version */}
          <div>
            <label className="text-xs text-text-muted block mb-1.5">Version Minecraft</label>
            <div className="relative">
              <button
                ref={triggerRef}
                type="button"
                onClick={() => setVersionDropdownOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2.5 bg-bg-primary border border-border-color rounded-lg text-sm text-text-primary hover:border-gaming-accent/50 focus:border-gaming-accent outline-none transition-colors"
              >
                <span>{version || 'Sélectionner une version'}</span>
                {loadingVersions ? (
                  <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
                ) : (
                  <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${versionDropdownOpen ? 'rotate-180' : ''}`} />
                )}
              </button>

              {versionDropdownOpen && dropdownPos && ReactDOM.createPortal(
                <div
                  ref={dropdownRef}
                  className="fixed z-[100] bg-bg-secondary border border-border-color rounded-lg shadow-xl overflow-hidden"
                  style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
                >
                  {/* Search + filter */}
                  <div className="p-2 border-b border-border-color space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
                      <input
                        type="text"
                        value={versionSearch}
                        onChange={(e) => setVersionSearch(e.target.value)}
                        placeholder="Rechercher..."
                        autoFocus
                        className="w-full pl-8 pr-3 py-1.5 bg-bg-primary border border-border-color rounded-md text-xs text-text-primary placeholder:text-text-muted focus:border-gaming-accent outline-none"
                      />
                    </div>
                    <div className="flex gap-1">
                      {(['release', 'snapshot', 'all'] as const).map((f) => (
                        <button
                          key={f}
                          type="button"
                          onClick={() => setVersionFilter(f)}
                          className={`flex-1 py-1 rounded-md text-[10px] font-medium transition-colors ${
                            versionFilter === f
                              ? 'bg-gaming-accent/15 text-gaming-accent border border-gaming-accent/30'
                              : 'bg-bg-primary text-text-muted border border-border-color hover:border-gaming-accent/20'
                          }`}
                        >
                          {f === 'release' ? 'Release' : f === 'snapshot' ? 'Snapshot' : 'Toutes'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Version list */}
                  <div className="max-h-72 overflow-y-auto gaming-scrollbar">
                    {filteredVersions.length === 0 ? (
                      <p className="text-xs text-text-muted text-center py-4">Aucune version trouvée</p>
                    ) : (
                      filteredVersions.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => { setVersion(v.id); setVersionDropdownOpen(false); setVersionSearch(''); }}
                          className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors ${
                            version === v.id
                              ? 'bg-gaming-accent/10 text-gaming-accent'
                              : 'text-text-primary hover:bg-bg-tertiary'
                          }`}
                        >
                          <span className="font-medium">{v.id}</span>
                          <span className={`text-[10px] ${v.type === 'release' ? 'text-emerald-400' : 'text-text-muted'}`}>
                            {v.type === 'release' ? 'Release' : v.type === 'snapshot' ? 'Snapshot' : v.type}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>

          {/* Loader */}
          <div>
            <label className="text-xs text-text-muted block mb-1.5">Mod Loader</label>
            <div className="grid grid-cols-4 gap-1.5">
              {LOADERS.map(l => (
                <button
                  key={l.id}
                  onClick={() => setLoader(l.id as any)}
                  className={`px-2 py-2 rounded-lg text-xs font-medium border text-center transition-all ${
                    loader === l.id
                      ? l.color
                      : 'border-border-color bg-bg-primary text-text-secondary hover:border-gaming-accent/30'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {/* Server info */}
          <div>
            <label className="text-xs text-text-muted block mb-1.5">Serveur (optionnel)</label>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <input
                  type="text"
                  value={serverAddress}
                  onChange={(e) => setServerAddress(e.target.value)}
                  placeholder="play.example.com"
                  className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-xs text-text-primary placeholder:text-text-muted focus:border-gaming-accent outline-none transition-colors"
                />
              </div>
              <input
                type="number"
                value={serverPort}
                onChange={(e) => setServerPort(e.target.value)}
                placeholder="25565"
                className="w-full px-3 py-2 bg-bg-primary border border-border-color rounded-lg text-xs text-text-primary placeholder:text-text-muted focus:border-gaming-accent outline-none transition-colors"
              />
            </div>
            {serverAddress && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoConnect}
                  onChange={(e) => setAutoConnect(e.target.checked)}
                  className="accent-gaming-accent w-3.5 h-3.5"
                />
                <span className="text-[11px] text-text-secondary">Auto-connect au lancement</span>
              </label>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border-color">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border-color text-sm text-text-secondary hover:text-text-primary hover:border-gaming-accent/30 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleCreate}
            disabled={!profileName.trim() || isCreating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gaming-accent text-white text-sm font-medium hover:bg-gaming-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
            Créer le profil
          </button>
        </div>
      </div>
    </div>
  , document.body);
};

export default CreateProfileModal;
